import React, { useState, useEffect } from 'react';
import { FaUsers, FaHandshake, FaShieldAlt } from 'react-icons/fa';
import HeroSlider from '../../components/HeroSlider';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { motion } from 'framer-motion';
import { collection, getDocs, query} from 'firebase/firestore';
import { db } from '../../config/firebase';

interface Member {
  id: string;
  fullName: string;
  photo: string;
  designation: string;
}

interface Gallery {
  id: string;
  title: string;
  category: string;
  featuredImageUrl: string;
}

const Home = () => {
  const missionRef = useScrollAnimation();
  const focusRef = useScrollAnimation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const membersQuery = query(
          collection(db, 'userDetails'),
        );
        const membersSnapshot = await getDocs(membersQuery);
        
        const membersData: Member[] = membersSnapshot.docs
          .map(doc => ({
            id: doc.id,
            fullName: doc.data().fullName?.split(' ')
              .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
              .join(' ') || 'Unknown',
            photo: doc.data().photo || '/default-avatar.png',
            memberType: doc.data().memberType?.charAt(0).toUpperCase() + doc.data().memberType?.slice(1).toLowerCase() || 'Member',
            rank: doc.data().rank || 'Member',
            designation: doc.data().postHeld?.toUpperCase() || 'MEMBER'
          }))
          .sort((a, b) => a.fullName.localeCompare(b.fullName));

        setMembers(membersData);
      } catch (error) {
        console.error('Error fetching members:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchGalleries = async () => {
      try {
        const galleriesQuery = query(
          collection(db, 'photo-gallery'),
        );
        const galleriesSnapshot = await getDocs(galleriesQuery);
        console.log('Galleries snapshot:', galleriesSnapshot.docs.map(doc => doc.data()));
        const galleriesData: Gallery[] = galleriesSnapshot.docs.map(doc => ({
          id: doc.id,
          title: doc.data().title,
          category: doc.data().category,
          featuredImageUrl: doc.data().featuredImageUrl,
        }));
        console.log('Mapped galleries data:', galleriesData);
        setGalleries(galleriesData);
      } catch (error) {
        console.error('Error fetching galleries:', error);
      }
    };

    const fetchData = async () => {
      try {
        await Promise.all([
          fetchMembers(),
          fetchGalleries()
        ]);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const getVisibleMembers = (): Member[] => {
    if (members.length === 0) return [];
    const visibleMembers: Member[] = [];
    for (let i = 0; i < 4; i++) {
      const index = (currentIndex + i) % members.length;
      visibleMembers.push({
        ...members[index],
      });
    }
    return visibleMembers;
  };

  useEffect(() => {
    let memberTimer: NodeJS.Timeout | undefined;
    if (members.length > 0) {
      memberTimer = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % members.length);
      }, 5000);
    }

    let galleryTimer: NodeJS.Timeout | undefined;
    if (galleries.length > 0) {
      galleryTimer = setInterval(() => {
        setCurrentGalleryIndex((prevIndex) => {
          const nextIndex = prevIndex + 1;
          // If we are about to go past the last original item, jump to the first duplicated item
          if (nextIndex >= galleries.length + 3) { // Changed from 4 to 3
            return 3; // Changed from 4 to 3
          }
          return nextIndex;
        });
      }, 5000);
    }

    return () => {
      if (memberTimer) clearInterval(memberTimer);
      if (galleryTimer) clearInterval(galleryTimer);
    };
  }, [members.length, galleries.length]);

  // Add duplicated galleries for seamless looping
  const loopedGalleries = React.useMemo(() => {
    if (galleries.length === 0) return [];
    const numToDuplicate = Math.min(galleries.length, 3); // Changed from 4 to 3
    const before = galleries.slice(-numToDuplicate);
    const after = galleries.slice(0, numToDuplicate);
    return [...before, ...galleries, ...after];
  }, [galleries]);

  // Adjust index when transitioning from duplicated items back to original
  useEffect(() => {
    if (galleries.length === 0) return;
    const numToDuplicate = Math.min(galleries.length, 3); // Changed from 4 to 3
    // If we are in the duplicated section at the end, jump back to the corresponding original item
    if (currentGalleryIndex >= galleries.length + numToDuplicate) {
      setCurrentGalleryIndex(numToDuplicate + (currentGalleryIndex - (galleries.length + numToDuplicate)));
    }
    // If we are in the duplicated section at the beginning, jump back to the corresponding original item
    if (currentGalleryIndex < numToDuplicate && currentGalleryIndex !== 0) {
       // This case is handled by the button/timer logic jumping to index 3
    }
     if (currentGalleryIndex === 0 && galleries.length > 0) {
         // Special case for initial load or manual jump to index 0
         // If there are items, jump to the last duplicated item to start the loop from the end
         setCurrentGalleryIndex(galleries.length + numToDuplicate);
     }

  }, [currentGalleryIndex, galleries.length]);

  console.log('Current galleries state:', galleries);

  return (
    <div className="bg-gradient-to-b from-gray-900 via-gray-800 to-black min-h-screen">
      {/* Hero Section */}
      <div className="relative h-screen">
        <HeroSlider />
      </div>

      {/* Focus Areas - Interactive Grid */}
      <div ref={focusRef} className="relative w-full min-h-fit py-16 my-16 md:min-h-screen md:py-24 md:my-24 overflow-x-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-800 to-black">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_50%_-100%,#f59e0b,transparent)] opacity-20" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_80%_60%,#fbbf24,transparent)] opacity-20" />
          </div>
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 md:pt-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-12 lg:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">Sustainable Solar Solutions</h2>
            <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-amber-500 to-yellow-500 mx-auto mb-4 sm:mb-6"></div>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto px-4">
              Transform your energy future with our cutting-edge solar solutions, delivering sustainable power and significant cost savings
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 lg:gap-8 max-w-6xl mx-auto">
            {[
              {
                title: "Residential Solar",
                items: ["Solar Panel Installation", "Battery Storage", "Smart Home Integration", "Energy Monitoring"],
                gradient: "from-amber-600 to-yellow-500"
              },
              {
                title: "Commercial Solutions",
                items: ["Industrial Systems", "Energy Management", "Power Purchase Agreements", "ROI Analysis"],
                gradient: "from-orange-600 to-amber-500"
              },
              {
                title: "Maintenance Services",
                items: ["System Monitoring", "Performance Optimization", "Preventive Maintenance", "24/7 Support"],
                gradient: "from-yellow-600 to-amber-500"
              },
              {
                title: "Green Innovation",
                items: ["Solar Technology", "Energy Efficiency", "Sustainable Design", "Carbon Reduction"],
                gradient: "from-amber-600 to-orange-500"
              }
            ].map((area, index) => (
              <motion.div
                key={area.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="group relative overflow-hidden rounded-xl sm:rounded-2xl bg-gray-800/30 hover:bg-gray-800/40 transition-colors duration-300"
              >
                {/* Fixing template string expression */}
                <div className={`absolute inset-0 bg-gradient-to-br ${area.gradient} opacity-10 group-hover:opacity-20 transition-opacity duration-300`} />
                <div className="relative p-4 sm:p-6 lg:p-8">
                  {/* Fixing template string expression */}
                  <h3 className={`text-xl sm:text-2xl font-bold text-white mb-4 sm:mb-6 group-hover:text-transparent bg-clip-text bg-gradient-to-r ${area.gradient} transition-colors duration-300`}>
                    {area.title}
                  </h3>
                  <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {area.items.map((item, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: i * 0.1 }}
                        className="flex items-center space-x-3 group/item"
                      >
                        <div className={`h-2 w-2 rounded-full bg-gradient-to-r ${area.gradient} group-hover/item:scale-125 transition-transform duration-300`} />
                        <span className="text-sm sm:text-base text-gray-300 group-hover/item:text-white transition-colors duration-300">
                          {item}
                        </span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Mission Section - Modern Cards */}
      <div ref={missionRef} className="relative min-h-[600px] py-16 sm:py-24 overflow-hidden flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-6 sm:mb-8"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">Illuminating Your Future with Solar Innovation</h2>
            <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-blue-500 to-purple-500 mx-auto"></div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: FaShieldAlt,
                title: "Energy Efficiency",
                description: "Maximize your energy savings with our advanced solar technology solutions",
                color: "from-blue-500 to-cyan-400"
              },
              {
                icon: FaUsers,
                title: "Sustainable Living",
                description: "Join the green revolution and reduce your carbon footprint with clean energy",
                color: "from-purple-500 to-pink-400"
              },
              {
                icon: FaHandshake,
                title: "Expert Support",
                description: "Professional installation and dedicated maintenance services for your solar system",
                color: "from-emerald-500 to-teal-400"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.2 }}
                className="group relative"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-gray-800/50 to-gray-900/50 rounded-2xl transform transition-all duration-300 group-hover:scale-[1.02]" />
                <div className="relative p-8 rounded-2xl backdrop-blur-sm border border-gray-700/50">
                  <div className={`inline-flex p-4 rounded-xl bg-gradient-to-r ${item.color} mb-6 transform transition-all duration-300 group-hover:scale-110`}>
                    <item.icon className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{item.title}</h3>
                  <p className="text-gray-300 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Projects Section - Horizontal Slider */}
      <div className="relative w-full py-36 sm:py-36 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900 via-gray-800 to-black opacity-80" />
        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 sm:mb-12 lg:mb-16"
          >
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">Our Projects</h2>
            <div className="w-20 sm:w-24 h-1 bg-gradient-to-r from-yellow-500 to-orange-500 mx-auto mb-4 sm:mb-6"></div>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl mx-auto px-4">
              Explore our completed solar projects and see the impact of sustainable energy solutions
            </p>
          </motion.div>

          <div className="relative">
            {loading ? (
              <div className="text-center">
                <p className="text-lg text-gray-300">Loading projects...</p>
              </div>
            ) : (
              <div className="flex overflow-hidden">
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{
                    transform: `translateX(-${currentGalleryIndex * (100 / 3)}%)`, // Changed from 4 to 3
                    width: `${loopedGalleries.length * (100 / 3)}%`, // Changed from 4 to 3
                  }}
                >
                  {loopedGalleries.map((gallery, index) => (
                    <div key={`${gallery.id}-${index}`} className="w-1/3 flex-shrink-0 px-2">
                      <div className="bg-gray-800 rounded-lg overflow-hidden shadow-lg text-center p-4">
                        <img
                          src={gallery.featuredImageUrl}
                          alt={gallery.title}
                          className="w-full h-48 object-cover mb-4 rounded-md"
                        />
                        <h3 className="text-lg font-semibold text-white mb-1">{gallery.title}</h3>
                        <p className="text-sm text-gray-400">{gallery.category}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {/* Navigation Buttons */}
            
            
          </div>
        </div>
      </div>

      {/* National Members Team Section */}
      <div className="relative min-h-screen py-16 overflow-hidden flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900 to-black">
          <div className="absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_500px_at_20%_0%,#3b82f6,transparent)] opacity-20" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_800px_at_80%_20%,#6366f1,transparent)] opacity-10" />
          </div>
        </div>

        <div className="relative w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 md:mb-6">Our Solar Experts</h2>
            <div className="w-20 md:w-24 h-1 bg-gradient-to-r from-blue-500 to-indigo-500 mx-auto mb-6 md:mb-8"></div>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto px-4">
            Meet our team of certified solar professionals dedicated to powering your sustainable future
            </p>
          </motion.div>

          <div className="relative w-full max-w-6xl mx-auto">
            {loading ? (
              <div className="text-center">
                <p className="text-lg text-gray-300">Loading...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {getVisibleMembers().map((member, index) => (
                  <div
                    key={`${currentIndex}-${index}`}
                    className="group relative aspect-[4/5] overflow-hidden rounded-2xl bg-gray-800"
                  >
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-300" />
                    
                    <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900">
                      <div className="absolute inset-0 opacity-80 group-hover:opacity-100 transition-opacity duration-300">
                        <img
                          src={member.photo}
                          alt={member.fullName}
                          className="h-full w-full object-cover object-center transform group-hover:scale-110 transition-transform duration-500"
                        />
                      </div>
                    </div>

                    <div className="absolute inset-0 flex flex-col justify-end p-4 md:p-6">
                      <div className="transform backdrop-blur-sm bg-black/50 p-3 md:p-4 rounded-lg translate-y-0 sm:translate-y-[200%] sm:group-hover:translate-y-0 transition-all duration-300 ease-out">
                        <h3 className="text-lg md:text-xl font-bold text-white mb-1 opacity-100">
                          {member.fullName}
                        </h3>
                        <p className="text-sm text-gray-400 opacity-100">
                          {member.designation}
                        </p>
                      </div>
                    </div>

                    <div className="absolute inset-0 border-2 border-transparent group-hover:border-blue-500/50 rounded-2xl transition-colors duration-300" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Home;
